import { useEffect, Suspense } from 'react';
import { lazyWithRetry as lazy } from '@/utils/lazyWithRetry';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ProductModeGuard } from '@/components/auth/ProductModeGuard';
import { SuperAdminRoute } from '@/components/auth/SuperAdminRoute';
import { AddonProtectedRoute } from '@/components/auth/AddonProtectedRoute';
import { ScrollToTop } from '@/components/common/ScrollToTop';
import { useCurrentCompany } from '@/lib/api/companies';
import { applyTheme } from '@/lib/theme';
import { getProductBrandName } from '@/lib/product-mode';
import { useTheme } from '@/hooks/useTheme';
import { useChunkLoadErrorHandler } from '@/hooks/useChunkLoadErrorHandler';
import { ListSkeleton } from '@/components/ui/LoadingSkeleton';
import { CookieConsent } from '@/components/gdpr/CookieConsent';
import { AudioProvider } from '@/contexts/AudioContext';
import { TourProvider } from '@/contexts/TourContext';
import { RealtimeManagerProvider } from '@/hooks/useRealtimeManager';
import { useActivityHeartbeat } from '@/hooks/useActivityHeartbeat';
import { trackPageView } from '@/lib/posthog';
import i18n from '@/lib/i18n';

// Lazy load all pages for code splitting
const LoginPage = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('@/pages/SignupPage').then(m => ({ default: m.SignupPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const AuthVerifyPage = lazy(() => import('@/pages/AuthVerifyPage').then(m => ({ default: m.AuthVerifyPage })));
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage').then(m => ({ default: m.AcceptInvitePage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const Reports = lazy(() => import('@/pages/Reports').then(m => ({ default: m.Reports })));
const OrganizationsList = lazy(() => import('@/pages/OrganizationsListNew').then(m => ({ default: m.OrganizationsListNew })));
const _OrganizationDetail = lazy(() => import('@/pages/OrganizationDetail').then(m => ({ default: m.OrganizationDetail })));
const OrganizationDetailWrapper = lazy(() => import('@/pages/OrganizationDetailWrapper').then(m => ({ default: m.OrganizationDetailWrapper })));
const ContactsList = lazy(() => import('@/pages/ContactsListNew').then(m => ({ default: m.ContactsListNew })));
const ContactDetail = lazy(() => import('@/pages/ContactDetail').then(m => ({ default: m.ContactDetail })));
const CallsListPage = lazy(() => import('@/pages/CallsList'));
const DealsList = lazy(() => import('@/pages/DealsListNew').then(m => ({ default: m.DealsListNew })));
const Pipeline = lazy(() => import('@/pages/Pipeline').then(m => ({ default: m.Pipeline })));
const TasksPage = lazy(() => import('@/pages/TasksPage').then(m => ({ default: m.TasksPage })));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail').then(m => ({ default: m.ProjectDetail })));
const WorkflowsList = lazy(() => import('@/pages/workflows/WorkflowsList').then(m => ({ default: m.WorkflowsList })));
const WorkflowBuilder = lazy(() => import('@/pages/workflows/WorkflowBuilder').then(m => ({ default: m.WorkflowBuilder })));
const WorkflowHistory = lazy(() => import('@/pages/workflows/WorkflowHistory').then(m => ({ default: m.WorkflowHistory })));
const SequencesList = lazy(() => import('@/pages/sequences/SequencesList').then(m => ({ default: m.default })));
const SequenceBuilder = lazy(() => import('@/pages/sequences/SequenceBuilder').then(m => ({ default: m.default })));
const SequenceEnrollments = lazy(() => import('@/pages/sequences/SequenceEnrollments').then(m => ({ default: m.default })));
const SequenceAnalytics = lazy(() => import('@/pages/sequences/SequenceAnalytics').then(m => ({ default: m.default })));
const TeamPage = lazy(() => import('@/pages/TeamPage').then(m => ({ default: m.TeamPage })));
const _UnmatchedEmails = lazy(() => import('@/pages/UnmatchedEmails'));
const MessagesInbox = lazy(() => import('@/pages/MessagesInbox'));
const EmailQualityPage = lazy(() => import('@/pages/EmailQualityPage'));
const DataQualityPage = lazy(() => import('@/pages/DataQualityPage'));
const AITestPage = lazy(() => import('@/pages/AITestPage').then(m => ({ default: m.AITestPage })));
const MorePage = lazy(() => import('@/pages/MorePage').then(m => ({ default: m.MorePage })));
const AssetsPage = lazy(() => import('@/pages/AssetsPage').then(m => ({ default: m.AssetsPage })));
const EmailAccounts = lazy(() => import('@/pages/settings/EmailAccounts').then(m => ({ default: m.EmailAccounts })));
const CompanySettings = lazy(() => import('@/pages/settings/CompanySettings').then(m => ({ default: m.CompanySettings })));
const ProfileSettings = lazy(() => import('@/pages/settings/ProfileSettings').then(m => ({ default: m.ProfileSettings })));
const FieldCustomization = lazy(() => import('@/pages/settings/FieldCustomization').then(m => ({ default: m.FieldCustomization })));
const DealSettings = lazy(() => import('@/pages/settings/DealSettings').then(m => ({ default: m.DealSettings })));
const ProjectManagerSettings = lazy(() => import('@/pages/settings/ProjectManagerSettings').then(m => ({ default: m.ProjectManagerSettings })));
const AddonsSettings = lazy(() => import('@/pages/settings/AddonsSettings').then(m => ({ default: m.AddonsSettings })));
const VoIPSettings = lazy(() => import('@/pages/settings/VoIPSettings').then(m => ({ default: m.VoIPSettings })));
const TranscriptionSettings = lazy(() => import('@/pages/settings/TranscriptionSettings').then(m => ({ default: m.TranscriptionSettings })));
const TwilioSettings = lazy(() => import('@/pages/settings/TwilioSettings').then(m => ({ default: m.TwilioSettings })));
const ApifySettings = lazy(() => import('@/pages/settings/ApifySettings').then(m => ({ default: m.ApifySettings })));
const ApifyActorSettings = lazy(() => import('@/pages/settings/ApifyActorSettings').then(m => ({ default: m.ApifyActorSettings })));
const EnrichmentSettings = lazy(() => import('@/pages/settings/EnrichmentSettings'));
const EnrichmentProviders = lazy(() => import('@/pages/settings/EnrichmentProviders'));
const ProviderApiKeysSettings = lazy(() => import('@/pages/settings/ProviderApiKeysSettings'));
// ResendSettings removed - /settings/resend now redirects to /settings/email-accounts (Phase 118)
const ReoonSettings = lazy(() => import('@/pages/settings/ReoonSettings').then(m => ({ default: m.ReoonSettings })));
const DomainHealthSettings = lazy(() => import('@/pages/settings/DomainHealthSettings').then(m => ({ default: m.DomainHealthSettings })));
const GoogleCalendarTasksSettings = lazy(() => import('@/pages/settings/GoogleCalendarTasksSettings').then(m => ({ default: m.GoogleCalendarTasksSettings })));
const SocialMediaSettings = lazy(() => import('@/pages/settings/SocialMediaSettings').then(m => ({ default: m.SocialMediaSettings })));
const SupportChannelsSettings = lazy(() => import('@/pages/settings/SupportChannelsSettings').then(m => ({ default: m.SupportChannelsSettings })));
const AdAnalyticsSettings = lazy(() => import('@/pages/settings/AdAnalyticsSettings').then(m => ({ default: m.AdAnalyticsSettings })));
const AdAnalyticsDashboard = lazy(() => import('@/pages/AdAnalyticsDashboard').then(m => ({ default: m.AdAnalyticsDashboard })));
const AdCampaignDetail = lazy(() => import('@/pages/AdCampaignDetail').then(m => ({ default: m.AdCampaignDetail })));
const AdGroupDetail = lazy(() => import('@/pages/AdGroupDetail').then(m => ({ default: m.AdGroupDetail })));
const SupportTemplatesSettings = lazy(() => import('@/pages/settings/SupportTemplatesSettings').then(m => ({ default: m.SupportTemplatesSettings })));
const VideoConferencingSettings = lazy(() => import('@/pages/settings/VideoConferencingSettings').then(m => ({ default: m.VideoConferencingSettings })));
const AccountingSyncSettings = lazy(() => import('@/pages/settings/AccountingSyncSettings').then(m => ({ default: m.AccountingSyncSettings })));
const ReplyAutomationSettings = lazy(() => import('@/pages/settings/ReplyAutomationSettings').then(m => ({ default: m.ReplyAutomationSettings }))); // Phase 165-06 route
const EcommerceIntegrationsSettings = lazy(() => import('@/pages/settings/EcommerceIntegrationsSettings').then(m => ({ default: m.EcommerceIntegrationsSettings })));
const SequencerSettings = lazy(() => import('@/pages/settings/SequencerSettings'));
const SocialDashboard = lazy(() => import('@/pages/social/SocialDashboard').then(m => ({ default: m.SocialDashboard })));
const SocialCalendar = lazy(() => import('@/pages/social/SocialCalendar').then(m => ({ default: m.SocialCalendar })));
const SocialCompose = lazy(() => import('@/pages/social/SocialCompose').then(m => ({ default: m.SocialCompose })));
const SocialPosts = lazy(() => import('@/pages/social/SocialPosts'));
const SocialEvents = lazy(() => import('@/pages/social/SocialEvents'));
const SocialBrands = lazy(() => import('@/pages/social/SocialBrands').then(m => ({ default: m.SocialBrands })));
const BrandDetail = lazy(() => import('@/pages/social/BrandDetail').then(m => ({ default: m.BrandDetail })));
const SocialDiscovery = lazy(() => import('@/pages/social/SocialDiscovery').then(m => ({ default: m.SocialDiscovery })));
const SocialRemix = lazy(() => import('@/pages/social/SocialRemix').then(m => ({ default: m.SocialRemix })));
const SocialPostDetail = lazy(() => import('@/pages/social/SocialPostDetail'));
const VideoCreator = lazy(() => import('@/pages/addons/VideoCreator').then(m => ({ default: m.VideoCreator })));
const WaveformUtility = lazy(() => import('@/pages/settings/WaveformUtility').then(m => ({ default: m.WaveformUtility })));
const GMapsGeniePage = lazy(() => import('@/pages/GMapsGeniePage').then(m => ({ default: m.GMapsGeniePage })));
const GMapsGenieV2Page = lazy(() => import('@/pages/GMapsGenieV2Page').then(m => ({ default: m.GMapsGenieV2Page })));
const LeadSearch = lazy(() => import('@/pages/LeadSearch'));
const DataImport = lazy(() => import('@/pages/settings/DataImport').then(m => ({ default: m.DataImport })));
const CSVImport = lazy(() => import('@/pages/settings/CSVImport'));
const BillingSettings = lazy(() => import('@/pages/settings/BillingSettings').then(m => ({ default: m.BillingSettings })));
const CreditsSettings = lazy(() => import('@/pages/settings/CreditsSettings').then(m => ({ default: m.CreditsSettings })));
const UsageDashboard = lazy(() => import('@/pages/settings/UsageDashboard').then(m => ({ default: m.UsageDashboard })));
const ApiKeysSettings = lazy(() => import('@/pages/settings/ApiKeysSettings').then(m => ({ default: m.ApiKeysSettings })));
const WebhooksSettings = lazy(() => import('@/pages/settings/WebhooksSettings').then(m => ({ default: m.WebhooksSettings })));
const SettingsLayout = lazy(() => import('@/components/settings/SettingsLayout').then(m => ({ default: m.SettingsLayout })));
const SettingsCardsView = lazy(() => import('@/pages/settings/SettingsCardsView').then(m => ({ default: m.SettingsCardsView })));
const Pricing = lazy(() => import('@/pages/Pricing').then(m => ({ default: m.Pricing })));
const VoIPPricing = lazy(() => import('@/pages/VoIPPricing').then(m => ({ default: m.VoIPPricing })));
const FAQ = lazy(() => import('@/pages/FAQ').then(m => ({ default: m.FAQ })));
const TestTwilioPage = lazy(() => import('@/pages/TestTwilioPage').then(m => ({ default: m.TestTwilioPage })));
const TestTwilioInteractive = lazy(() => import('@/pages/TestTwilioInteractive').then(m => ({ default: m.TestTwilioInteractive })));
const TestTwimlBin = lazy(() => import('@/pages/TestTwimlBin').then(m => ({ default: m.TestTwimlBin })));
const AudioOutputTest = lazy(() => import('@/pages/AudioOutputTest'));
const FormBuilder = lazy(() => import('@/pages/FormBuilder'));
const FormsList = lazy(() => import('@/pages/FormsList'));
const FeedbackList = lazy(() => import('@/pages/FeedbackList'));
const FeedbackLayout = lazy(() => import('@/pages/FeedbackLayout'));
const Leaderboard = lazy(() => import('@/pages/forms/Leaderboard'));
const Goals = lazy(() => import('@/pages/forms/Goals'));
const Inbox = lazy(() => import('@/pages/forms/Inbox'));
const InboxDetail = lazy(() => import('@/pages/forms/InboxDetail'));
const SentimentDashboard = lazy(() => import('@/pages/forms/SentimentDashboard').then(m => ({ default: m.SentimentDashboard })));
const ReportsListPage = lazy(() => import('@/pages/forms/ReportsListPage').then(m => ({ default: m.ReportsListPage })));
const ReportDetailPage = lazy(() => import('@/pages/forms/ReportDetailPage').then(m => ({ default: m.ReportDetailPage })));
const ActionLoopList = lazy(() => import('@/pages/feedback/ActionLoopList'));
const MagicLinkLandingPage = lazy(() => import('@/pages/MagicLinkLandingPage').then(m => ({ default: m.MagicLinkLandingPage })));
// Phase 159-04: dormant cross-TLD auth handoff + upgrade welcome destination (D-09/D-10)
const AuthHandoffPage = lazy(() => import('@/pages/AuthHandoffPage').then(m => ({ default: m.AuthHandoffPage })));
const OnboardingWelcomePage = lazy(() => import('@/pages/OnboardingWelcomePage').then(m => ({ default: m.OnboardingWelcomePage })));
const PublicForm = lazy(() => import('@/pages/PublicForm'));
const EmbedForm = lazy(() => import('@/pages/EmbedForm'));
const TestAudioPage = lazy(() => import('@/pages/TestAudioPage').then(m => ({ default: m.TestAudioPage })));
// Super Admin pages
const SuperAdminDashboard = lazy(() => import('@/pages/superadmin/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const CompanyDetail = lazy(() => import('@/pages/superadmin/CompanyDetail').then(m => ({ default: m.CompanyDetail })));
const AddonAPITestPage = lazy(() => import('@/pages/superadmin/AddonAPITestPage').then(m => ({ default: m.AddonAPITestPage })));
const AddonCatalogManager = lazy(() => import('@/pages/superadmin/AddonCatalogManager').then(m => ({ default: m.AddonCatalogManager })));
// Phase 165-08 route
const AiUsagePage = lazy(() => import('@/pages/superadmin/AiUsagePage').then(m => ({ default: m.AiUsagePage })));
const AddonGrantsPage = lazy(() => import('@/pages/superadmin/AddonGrantsPage').then(m => ({ default: m.AddonGrantsPage })));
const FunnelBaselinesPage = lazy(() => import('@/pages/superadmin/FunnelBaselinesPage').then(m => ({ default: m.FunnelBaselinesPage })));
const InboxSyncPage = lazy(() => import('@/pages/superadmin/InboxSyncPage').then(m => ({ default: m.InboxSyncPage })));
const BillingManager = lazy(() => import('@/pages/superadmin/BillingManager').then(m => ({ default: m.BillingManager })));
const CreditManager = lazy(() => import('@/pages/superadmin/CreditManager').then(m => ({ default: m.CreditManager })));
const CreditsPricingManager = lazy(() => import('@/pages/superadmin/CreditsPricingManager').then(m => ({ default: m.CreditsPricingManager })));
const VoIPPricingManager = lazy(() => import('@/pages/superadmin/VoIPPricingManager').then(m => ({ default: m.VoIPPricingManager })));
const TwilioPriceDebug = lazy(() => import('@/pages/superadmin/TwilioPriceDebug').then(m => ({ default: m.TwilioPriceDebug })));
const StripeManager = lazy(() => import('@/pages/superadmin/StripeManager').then(m => ({ default: m.StripeManager })));
const AppSettings = lazy(() => import('@/pages/superadmin/AppSettings').then(m => ({ default: m.AppSettings })));
const AuthEmailSettings = lazy(() => import('@/pages/superadmin/AuthEmailSettings').then(m => ({ default: m.AuthEmailSettings })));
const EnrichmentManager = lazy(() => import('@/pages/superadmin/EnrichmentManager').then(m => ({ default: m.EnrichmentManager })));
const PageLayoutsManager = lazy(() => import('@/pages/superadmin/PageLayoutsManager').then(m => ({ default: m.PageLayoutsManager })));
const AISettingsManager = lazy(() => import('@/pages/superadmin/AISettingsManager').then(m => ({ default: m.AISettingsManager })));
const GmapsProviderSettings = lazy(() => import('@/pages/superadmin/GmapsProviderSettings').then(m => ({ default: m.GmapsProviderSettings })));
const UnifiedPricingDemo = lazy(() => import('@/pages/superadmin/UnifiedPricingDemo').then(m => ({ default: m.UnifiedPricingDemo })));
const SupportMessages = lazy(() => import('@/pages/support/SupportMessages'));
const SupportMessagesDemo = lazy(() => import('@/pages/support/SupportMessagesDemo'));
const CompanyDetailsCardDemo = lazy(() => import('@/pages/demo/CompanyDetailsCardDemo'));
const MissionControlDemo = lazy(() => import('@/pages/superadmin/MissionControlDemo'));
const MissionControl = lazy(() => import('@/pages/mission-control/MissionControlPage'));
const CompanyDetailsCardDemo2 = lazy(() => import('@/pages/demo/CompanyDetailsCardDemo2'));
const EnrichmentCardsDemo = lazy(() => import('@/pages/demo/EnrichmentCardsDemo'));
const TaskHoverCardDemo = lazy(() => import('@/pages/demos/TaskHoverCardDemo'));
const SceneProgressDemo = lazy(() => import('@/pages/demo/SceneProgressDemo'));
const SceneProgressAltDemo = lazy(() => import('@/pages/demo/SceneProgressAltDemo'));

// Redirect helper: preserves :tab and :id params when redirecting old /forms/inbox/:tab/:id
function ForwardInboxDetail() {
  const { tab, id } = useParams();
  return <Navigate to={'/feedback/inbox/' + tab + '/' + id} replace />;
}

// Redirect helper: preserves :funnelId param when redirecting old /forms/sentiment/:funnelId
function ForwardSentimentDetail() {
  const { funnelId } = useParams();
  return <Navigate to={'/feedback/sentiment/' + funnelId} replace />;
}

// Loading fallback component
function PageLoader() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <ListSkeleton count={3} />
    </div>
  );
}

function App() {
  const { data: company } = useCurrentCompany();
  const location = useLocation();

  // Initialize dark mode theme system and get current resolved theme
  const { resolvedTheme } = useTheme();

  // Handle chunk loading errors (detects new deployments and prompts refresh)
  useChunkLoadErrorHandler();

  // Track user activity for superadmin "last active" visibility
  useActivityHeartbeat();

  // Apply company branding theme when company data or theme changes
  useEffect(() => {
    applyTheme(company || null, resolvedTheme);
  }, [company, resolvedTheme]);

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Update document title on route changes
  useEffect(() => {
    const APP = getProductBrandName();
    const PATH_TITLES: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/reports': 'Reports',
      '/deals': 'Deals',
      '/pipeline': 'Pipeline',
      '/tasks': 'Tasks',
      '/workflows': 'Workflows',
      '/sequences': 'Sequences',
      '/organizations': 'Organizations',
      '/contacts': 'Contacts',
      '/calls': 'Calls',
      '/messages': 'Messages',
      '/assets': 'Assets',
      '/more': 'More',
      '/lead-search': 'Lead Search',
      '/brands': 'Brands',
      '/social': 'Social Dashboard',
      '/social/calendar': 'Social Calendar',
      '/social/compose': 'Compose Post',
      '/social/posts': 'Social Posts',
      '/social/events': 'Social Events',
      '/social/discovery': 'Discovery',
      '/social/remix': 'Remix',
      '/projects': 'Projects',
      '/support': 'Support',
      '/faq': 'FAQ',
      '/addons/gmaps-genie': 'Google Maps Genie',
      '/addons/gmaps-genie2': 'Google Maps Genie',
      '/addons/video-creator': 'Video Creator',
      '/data-quality': 'Data Quality',
      '/emails/quality': 'Email Quality',
      '/login': 'Sign In',
      '/signup': 'Sign Up',
      '/forgot-password': 'Forgot Password',
      '/reset-password': 'Reset Password',
      '/pricing': 'Pricing',
      '/pricing/voip': 'VoIP Pricing',
      '/settings': 'Settings',
      '/settings/profile': 'Profile · Settings',
      '/settings/team': 'Team · Settings',
      '/settings/company': 'Company · Settings',
      '/settings/email-accounts': 'Email Accounts · Settings',
      '/settings/voip': 'VoIP · Settings',
      '/settings/addons': 'Add-ons · Settings',
      '/settings/billing': 'Billing · Settings',
      '/settings/api-keys': 'API Keys · Settings',
      '/settings/webhooks': 'Webhooks · Settings',
      '/settings/data-import': 'Data Import · Settings',
      '/super-admin': 'Super Admin',
    };

    const path = location.pathname;
    // Exact match first, then prefix match for nested routes
    const title =
      PATH_TITLES[path] ??
      Object.entries(PATH_TITLES)
        .filter(([k]) => k !== '/' && path.startsWith(k + '/'))
        .sort((a, b) => b[0].length - a[0].length)[0]?.[1];

    // Only set title from map if no page-level usePageTitle is active
    // (detail pages call usePageTitle which sets its own title)
    if (title) {
      document.title = `${title} · ${APP}`;
    } else if (!path.match(/\/(organizations|contacts|projects|workflows|sequences|super-admin\/companies)\/[^/]+/)) {
      document.title = APP;
    }
  }, [location.pathname]);

  // Note: Do NOT manually clear URL hash containing access_token here.
  // Supabase JS handles hash cleanup internally after processing OAuth tokens.
  // Clearing it early creates a race condition where the hash is removed before
  // Supabase's async _initialize() can read it, breaking Google sign-in.

  // Clean up internal cache-busting/reload query params after PWA/chunk reloads
  useEffect(() => {
    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has('_refresh')) { url.searchParams.delete('_refresh'); changed = true; }
    if (url.searchParams.has('_cb')) { url.searchParams.delete('_cb'); changed = true; }
    if (changed) window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <Suspense fallback={<div className="flex h-screen items-center justify-center">
        <div className="text-content-secondary">Loading translations...</div>
      </div>}>
        <RealtimeManagerProvider>
        <AudioProvider>
          <TourProvider>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/verify" element={<AuthVerifyPage />} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/pricing/voip" element={<VoIPPricing />} />
          <Route path="/f/:slug" element={<PublicForm />} />
          <Route path="/embed/:slug" element={<EmbedForm />} />
          {/* Plan 09: magic-link landing page (public, no auth required) */}
          <Route path="/r/:token" element={<MagicLinkLandingPage />} />
          {/* Phase 159-04: dormant handoff consumer - sets its own session via hash tokens (D-09/D-10) */}
          <Route path="/auth/handoff" element={<AuthHandoffPage />} />
          {/* Phase 159-04: dormant upgrade welcome destination - nothing links here in v1.7 (D-09/D-10) */}
          <Route path="/onboarding/welcome" element={<OnboardingWelcomePage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<ProductModeGuard />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/deals" element={<DealsList />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/workflows" element={<WorkflowsList />} />
        <Route path="/workflows/new" element={<WorkflowBuilder />} />
        <Route path="/workflows/:id/edit" element={<WorkflowBuilder />} />
        <Route path="/workflows/:id/history" element={<WorkflowHistory />} />
        <Route path="/sequences" element={<SequencesList />} />
        <Route path="/sequences/analytics" element={<SequenceAnalytics />} />
        <Route path="/sequences/new" element={<SequenceBuilder />} />
        <Route path="/sequences/:id/edit" element={<SequenceBuilder />} />
        <Route path="/sequences/:id/enrollments" element={<SequenceEnrollments />} />
        {/* Redirect old /team URL to new location inside settings */}
        <Route path="/team" element={<Navigate to="/settings/team" replace />} />
        <Route path="/organizations" element={<OrganizationsList />} />
        <Route path="/organizations/:id" element={<OrganizationDetailWrapper />} />
        <Route path="/contacts" element={<ContactsList />} />
        <Route path="/contacts/:id" element={<ContactDetail />} />
        <Route path="/calls" element={<CallsListPage />} />
        <Route path="/messages" element={<MessagesInbox />} />
        <Route path="/emails/quality" element={<EmailQualityPage />} />
        <Route path="/data-quality" element={<DataQualityPage />} />
        {/* Legacy route - redirect to new messages inbox */}
        <Route path="/emails/unmatched" element={<Navigate to="/messages?folder=unmatched" replace />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/assets" element={<AssetsPage />} />

        {/* Settings - Nested routes with unified layout */}
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<SettingsCardsView />} />
          <Route path="profile" element={<ProfileSettings />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="company" element={<CompanySettings />} />
          <Route path="email-accounts" element={<EmailAccounts />} />
          {/* Email Templates moved to Messages inbox - redirect old URL */}
          <Route path="email-templates" element={<Navigate to="/messages?folder=templates" replace />} />
          <Route path="voip" element={<VoIPSettings />} />
          <Route path="transcription" element={<TranscriptionSettings />} />
          <Route path="twilio" element={<TwilioSettings />} />
          <Route path="apify" element={<ApifySettings />} />
          <Route path="apify-actors" element={<ApifyActorSettings />} />
          <Route path="enrichment" element={<EnrichmentSettings />} />
          <Route path="enrichment/providers" element={<EnrichmentProviders />} />
          <Route path="provider-api-keys" element={<ProviderApiKeysSettings />} />
          <Route path="resend" element={<Navigate to="/settings/email-accounts" replace />} />
          <Route path="reoon" element={<ReoonSettings />} />
          <Route path="domain-health" element={<DomainHealthSettings />} />
          <Route path="google-calendar-tasks" element={<GoogleCalendarTasksSettings />} />
          <Route path="social-media" element={<SocialMediaSettings />} />
          <Route path="support-channels" element={<SupportChannelsSettings />} />
          <Route path="ad-analytics" element={<AdAnalyticsSettings />} />
          <Route path="support-templates" element={<SupportTemplatesSettings />} />
          <Route path="video-conferencing" element={<VideoConferencingSettings />} />
          <Route path="accounting-sync" element={<AccountingSyncSettings />} />
          <Route path="reply-automation" element={<ReplyAutomationSettings />} /> {/* Phase 165-06 route */}
          <Route path="ecommerce-integrations" element={<EcommerceIntegrationsSettings />} />
          <Route path="sequencer" element={<SequencerSettings />} />
          <Route path="signature-template" element={<Navigate to="/settings/email-accounts?tab=signatures" replace />} />
          <Route path="field-customization" element={<FieldCustomization />} />
          <Route path="deals" element={<DealSettings />} />
          <Route path="pipeline" element={<Navigate to="/settings/deals" replace />} />
          <Route path="addons" element={<AddonsSettings />} />
          <Route path="project-manager" element={<ProjectManagerSettings />} />
          <Route path="billing" element={<BillingSettings />} />
          <Route path="credits" element={<CreditsSettings />} />
          <Route path="usage" element={<UsageDashboard />} />
          <Route path="api-keys" element={<ApiKeysSettings />} />
          <Route path="webhooks" element={<WebhooksSettings />} />
          <Route path="data-import" element={<DataImport />} />
          <Route path="csv-import" element={<CSVImport />} />
        </Route>

        {/* Addon-protected routes outside settings */}
        <Route
          path="/addons/gmaps-genie"
          element={
            <AddonProtectedRoute addonSlug="gmaps-genie">
              <GMapsGeniePage />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/addons/gmaps-genie2"
          element={
            <AddonProtectedRoute addonSlug="gmaps-genie">
              <GMapsGenieV2Page />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/lead-search"
          element={
            <AddonProtectedRoute addonSlug="lead-search">
              <LeadSearch />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <AddonProtectedRoute addonSlug="project-manager">
              <ProjectsPage />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <AddonProtectedRoute addonSlug="project-manager">
              <ProjectDetail />
            </AddonProtectedRoute>
          }
        />
        {/* Social Manager Routes */}
        <Route
          path="/social"
          element={
            <AddonProtectedRoute addonSlug="social-manager">
              <SocialDashboard />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/social/calendar"
          element={
            <AddonProtectedRoute addonSlug="social-manager">
              <SocialCalendar />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/social/compose"
          element={
            <AddonProtectedRoute addonSlug="social-manager">
              <SocialCompose />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/social/posts/:id"
          element={
            <AddonProtectedRoute addonSlug="social-manager">
              <SocialPostDetail />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/social/posts"
          element={
            <AddonProtectedRoute addonSlug="social-manager">
              <SocialPosts />
            </AddonProtectedRoute>
          }
        />
        {/* Legacy redirect */}
        <Route path="/social/drafts" element={<Navigate to="/social/posts?status=draft" replace />} />
        <Route
          path="/social/events"
          element={
            <AddonProtectedRoute addonSlug="social-manager">
              <SocialEvents />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/social/discovery"
          element={
            <AddonProtectedRoute addonSlug="social-manager">
              <SocialDiscovery />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/social/remix"
          element={
            <AddonProtectedRoute addonSlug="social-manager">
              <SocialRemix />
            </AddonProtectedRoute>
          }
        />
        <Route path="/brands" element={<SocialBrands />} />
        <Route path="/brands/:id" element={<BrandDetail />} />
        {/* Video Creator Routes */}
        <Route
          path="/addons/video-creator"
          element={
            <AddonProtectedRoute addonSlug="video-creator">
              <VideoCreator />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/addons/video-creator/:projectId"
          element={
            <AddonProtectedRoute addonSlug="video-creator">
              <VideoCreator />
            </AddonProtectedRoute>
          }
        />
        {/* Ad Analytics Routes */}
        <Route
          path="/ad-analytics"
          element={
            <AddonProtectedRoute addonSlug="ad-analytics">
              <AdAnalyticsDashboard />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/ad-analytics/campaign/:campaignId"
          element={
            <AddonProtectedRoute addonSlug="ad-analytics">
              <AdCampaignDetail />
            </AddonProtectedRoute>
          }
        />
        <Route
          path="/ad-analytics/ad-group/:adGroupId"
          element={
            <AddonProtectedRoute addonSlug="ad-analytics">
              <AdGroupDetail />
            </AddonProtectedRoute>
          }
        />
        {/* Support Messages Routes */}
        <Route
          path="/support"
          element={
            <AddonProtectedRoute addonSlug="marketplace-support">
              <SupportMessages />
            </AddonProtectedRoute>
          }
        />
        {/* Form Builder Routes - /forms/leaderboard|goals|inbox|sentiment redirect to /feedback/* */}
        <Route path="/forms/leaderboard" element={<Navigate to="/feedback/leaderboard" replace />} />
        <Route path="/forms/goals" element={<Navigate to="/feedback/goals" replace />} />
        <Route path="/forms/inbox" element={<Navigate to="/feedback/inbox" replace />} />
        <Route path="/forms/inbox/:tab/:id" element={<ForwardInboxDetail />} />
        <Route path="/forms/sentiment" element={<Navigate to="/feedback/sentiment" replace />} />
        <Route path="/forms/sentiment/:funnelId" element={<ForwardSentimentDetail />} />
        <Route path="/forms" element={<FormsList />} />
        <Route path="/forms/new" element={<FormBuilder />} />
        <Route path="/forms/:id/edit" element={<FormBuilder />} />
        <Route path="/forms/builder/:id" element={<FormBuilder />} />
        {/* Feedback (review funnels) Routes */}
        <Route element={<FeedbackLayout />}>
          <Route path="/feedback" element={<FeedbackList />} />
          <Route path="/feedback/leaderboard" element={<Leaderboard />} />
          <Route path="/feedback/goals" element={<Goals />} />
          <Route path="/feedback/inbox" element={<Inbox />} />
          <Route path="/feedback/inbox/:tab/:id" element={<InboxDetail />} />
          <Route path="/feedback/sentiment" element={<SentimentDashboard />} />
          <Route path="/feedback/sentiment/:funnelId" element={<SentimentDashboard />} />
          <Route path="/feedback/reports" element={<ReportsListPage />} />
          <Route path="/feedback/reports/:reportId" element={<ReportDetailPage />} />
          <Route path="/feedback/actions" element={<ActionLoopList />} />
          <Route path="/feedback/actions/cycle/:cyclicReportId" element={<ActionLoopList />} />
        </Route>
        <Route path="/feedback/new" element={<FormBuilder />} />
        <Route path="/feedback/:id/edit" element={<FormBuilder />} />
        <Route path="/feedback/builder/:id" element={<FormBuilder />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/ai-test" element={<AITestPage />} />
        <Route path="/test-twilio" element={<TestTwilioPage />} />
        <Route path="/test-twilio-interactive" element={<TestTwilioInteractive />} />
        <Route path="/test-twiml-bin" element={<TestTwimlBin />} />
        <Route path="/test/audio-output" element={<AudioOutputTest />} />
        <Route path="/test-audio" element={<TestAudioPage />} />
          </Route>{/* ProductModeGuard */}
      </Route>{/* ProtectedRoute */}

          {/* Super Admin Routes */}
          <Route element={<SuperAdminRoute />}>
            <Route path="/super-admin" element={<SuperAdminDashboard />} />
            <Route path="/super-admin/companies/:companyId" element={<CompanyDetail />} />
            <Route path="/super-admin/addons" element={<AddonCatalogManager />} />
            <Route path="/super-admin/addon-grants" element={<AddonGrantsPage />} />
            <Route path="/super-admin/ai-usage" element={<AiUsagePage />} />
            <Route path="/super-admin/funnel-baselines" element={<FunnelBaselinesPage />} />
            <Route path="/super-admin/inbox-sync" element={<InboxSyncPage />} />
            <Route path="/super-admin/api-tester" element={<AddonAPITestPage />} />
            <Route path="/super-admin/billing" element={<BillingManager />} />
            <Route path="/super-admin/credits" element={<CreditManager />} />
            <Route path="/super-admin/credits-pricing" element={<CreditsPricingManager />} />
            <Route path="/super-admin/voip-pricing" element={<VoIPPricingManager />} />
            <Route path="/super-admin/twilio-debug" element={<TwilioPriceDebug />} />
            <Route path="/super-admin/stripe" element={<StripeManager />} />
            <Route path="/super-admin/waveform-utility" element={<WaveformUtility />} />
            <Route path="/super-admin/app-settings" element={<AppSettings />} />
            <Route path="/super-admin/email-templates" element={<AuthEmailSettings />} />
            <Route path="/super-admin/enrichment" element={<EnrichmentManager />} />
            <Route path="/super-admin/page-layouts" element={<PageLayoutsManager />} />
            <Route path="/super-admin/ai-settings" element={<AISettingsManager />} />
            <Route path="/super-admin/gmaps-provider" element={<GmapsProviderSettings />} />
            <Route path="/super-admin/unified-pricing-demo" element={<UnifiedPricingDemo />} />
            <Route path="/super-admin/support-demo" element={<SupportMessagesDemo />} />
            <Route path="/super-admin/company-details-demo" element={<CompanyDetailsCardDemo />} />
            <Route path="/super-admin/company-details-demo-2" element={<CompanyDetailsCardDemo2 />} />
            <Route path="/super-admin/enrichment-cards-demo" element={<EnrichmentCardsDemo />} />
            <Route path="/super-admin/task-hover-card-demo" element={<TaskHoverCardDemo />} />
            <Route path="/demo/scene-progress" element={<SceneProgressDemo />} />
            <Route path="/demo/scene-progress-alt" element={<SceneProgressAltDemo />} />
            <Route path="/super-admin/mission-control-demo" element={<MissionControlDemo />} />
            <Route path="/mission-control" element={<MissionControl />} />
          </Route>

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </Suspense>

            {/* Cookie Consent Banner */}
            <CookieConsent />

            {/* App Update Notification */}
          </TourProvider>
        </AudioProvider>
        </RealtimeManagerProvider>
      </Suspense>
    </I18nextProvider>
  );
}

export default App;
